// TYPES
// COMPONENTS
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { organizationNodesSelector } from 'slices/organizationHierarchy.slice';
// ACTIONS
import { rolesSelector } from 'slices/roles.slice';
import type { TAttributeValueArrayPair } from '@timeedit/types/lib/types';
import { Button, EditableTable, Icon, Label } from '@timeedit/ui-components';
// STYLES
import { Select, Space } from '@timeedit/ui-components/antd';
// i18N
import intl from '../../../i18n/intl';
import './index.scss';

const language: any = intl.messages;

type Props = {
  organizationNodesAndRoles: TAttributeValueArrayPair[];
  onChange: (organizationsAndRoles: TAttributeValueArrayPair[]) => void;
  title: string;
  tooltip: string;
  maxRows?: boolean | number;
  minRows?: boolean | number;
  disabled?: boolean;
};

const OrganizationNodesAndRolesTable = ({
  organizationNodesAndRoles = [],
  onChange,
  title,
  tooltip,
  maxRows = false,
  minRows = false,
  disabled = false,
}: Props) => {
  const allRoles = useSelector(rolesSelector);
  const allOrganizationNodes = useSelector(organizationNodesSelector);

  const onUpdateRow = (colIdx: number, rowIdx: number, value: string) => {
    const oldRow = organizationNodesAndRoles[rowIdx];
    const updRow: TAttributeValueArrayPair = [
      colIdx === 0 ? value : oldRow[0],
      colIdx === 1 ? value : oldRow[1],
    ];
    const updatedArr = [
      ...organizationNodesAndRoles.slice(0, rowIdx),
      updRow,
      ...organizationNodesAndRoles.slice(rowIdx + 1),
    ];
    onChange(updatedArr);
  };

  const onDeleteRow = (rowIdx: number) => {
    const updatedArr = [
      ...organizationNodesAndRoles.slice(0, rowIdx),
      ...organizationNodesAndRoles.slice(rowIdx + 1),
    ];
    onChange(updatedArr);
  };

  const onAddRow = () => {
    onChange([...organizationNodesAndRoles, ['', '']]);
  };

  const columns = [
    {
      title: 'Organization node',
      dataIndex: 'organization',
      key: 'organization',
      width: 260,
      render: (_: any, row: TAttributeValueArrayPair, rowIdx: any) => (
        <Select
          data-testid={`organization-node-${rowIdx}`}
          size="small"
          value={row && row[0] ? row[0] : undefined}
          placeholder="Select an organization node"
          onChange={(value) => onUpdateRow(0, rowIdx, value)}
          disabled={disabled}
          showSearch
          options={allOrganizationNodes.map((node) => ({
            label: node.name,
            value: node.id,
          }))}
          style={{ width: '100%' }}
          filterOption={(inputValue, option: any) => {
            return option?.label
              ?.toLowerCase()
              .includes(inputValue?.toLowerCase());
          }}
        />
      ),
    },
    {
      title: 'Role',
      dataIndex: undefined,
      key: 'role',
      width: 260,
      render: (_: any, row: any, rowIdx: number) => (
        <Select
          data-testid={`role-${rowIdx}`}
          size="small"
          value={row && row[1] ? row[1] : undefined}
          onChange={(value) => onUpdateRow(1, rowIdx, value)}
          placeholder="Select a role"
          disabled={disabled}
          showSearch
          style={{ width: '100%' }}
          filterOption={(inputValue, option: any) =>
            option?.label?.toLowerCase().includes(inputValue?.toLowerCase())
          }
          options={allRoles.map((role) => ({
            label: role.name,
            value: role.name,
          }))}
        />
      ),
    },
    {
      title: '',
      dataIndex: undefined,
      key: 'action',
      render: (val: any, record: any, rowIdx: number) => (
        <Button
          size="small"
          type="text"
          className="types-list-action-button-delete"
          onClick={() => onDeleteRow(rowIdx)}
          icon={<Icon id="DeleteOutlined" alt="Delete" />}
          disabled={
            (minRows !== false &&
              organizationNodesAndRoles.length <= (minRows as number)) ||
            disabled
          }
        />
      ),
    },
  ];

  const footer = useCallback(
    () => (
      <Space>
        <Button
          type="primary"
          size="small"
          onClick={onAddRow}
          disabled={
            (maxRows !== false &&
              organizationNodesAndRoles.length >= (maxRows as number)) ||
            disabled
          }
        >
          {language.add_permission}
        </Button>
      </Space>
    ),
    [organizationNodesAndRoles, maxRows, disabled]
  );

  return (
    <div className="organization-nodes-roles">
      <Label title={title} tooltip={tooltip} />
      <div className="permissions">
        <EditableTable
          dataSource={organizationNodesAndRoles}
          columns={columns}
          footer={footer}
        />
      </div>
    </div>
  );
};

export default OrganizationNodesAndRolesTable;
